import { AfterViewInit, Component, HostListener, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from './auth/services/auth.service';
import { NavigationEnd, NavigationStart, Router, ActivatedRoute } from '@angular/router';
import { SharedService } from './shared/serviceLayer/shared.service';
import { LanguageService } from './shared/serviceLayer/language.service';
import { SeoService } from './shared/serviceLayer/seo.service';
import Clarity from '@microsoft/clarity';
import { environment } from '../environments/environment';

declare var Tawk_API: any;

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit {
  title = 'aca-web';
  status = 'ONLINE';
  isConnected = true;
  currentUser: any;
  showLoader = false;
  isChatScriptLoaded = false;
  hideChatButton = false;

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(e: KeyboardEvent) {
    if (e.key === 'F12') { return false; }
    if (e.ctrlKey && e.shiftKey && e.key === 'I') { return false; }
    if (e.ctrlKey && e.shiftKey && e.key === 'C') { return false; }
    if (e.ctrlKey && e.shiftKey && e.key === 'J') { return false; }
    if (e.ctrlKey && e.key === 'U') { return false; }
    return true;
  }

  constructor(
    private authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private sharedService: SharedService,
    private languageService: LanguageService,
    private seoService: SeoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.languageService.initLanguage();
    if (isPlatformBrowser(this.platformId)) {
      this.currentUser = this.authService.currentUserValue;
    }
  }

  ngOnInit(): void {
    // this.initClarity();
    this.checkButtonState();
    this.detectLanguageFromUrl();
    this.setupSEO();
    this.subscribeToLanguageChanges();
    this.router.events.subscribe(event => this.handleRouterEvents(event));
    this.applyChatWidgetVisibility(this.router.url);

    if (this.currentUser?.token) {
      // this.sseService.createEventSource();
    }
  }

  private initClarity(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const projectId = environment.clarityProjectId;
    if (!projectId) return;
    Clarity.init(projectId);
  }

  private detectLanguageFromUrl(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const url = this.router.url;
    const storedLang = localStorage.getItem('lang') || 'eng';
    if (url.startsWith('/ar') && storedLang !== 'ara') {
      this.languageService.setLanguage('ara');
    }
  }

  private setupSEO(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateSEOTags(event.url);
      }
    });
  }

  private subscribeToLanguageChanges(): void {
    this.languageService.language$.subscribe(() => {
      this.updateSEOTags(this.router.url);
    });
  }

  private updateSEOTags(url: string): void {
    setTimeout(() => {
      const currentLang = this.languageService.getCurrentLang();
      const isArabic = currentLang === 'ara' || url.startsWith('/ar');
      this.seoService.update(url, isArabic);
    }, 100);
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkButtonState();
      }
    });
  }

  handleRouterEvents(event: any) {
    if (event instanceof NavigationEnd) {
      this.updateSEOTags(event.url);
      this.handleNavigationEnd(event.url);
      this.showLoader = !event.url.includes('auction-join') && !event.url.includes('banner');
      this.sharedService.setIsShowHeader(!event.url.includes('auction-join') && !event.url.includes('auctions'));
    }
    if (event instanceof NavigationStart) {
      this.showLoader = !event.url.includes('auction-join') && !event.url.includes('banner');
    }
  }

  onChatButtonClick() {
    this.loadTawkToScript();
    localStorage.setItem('chatButtonClicked', 'true');
    this.hideChatButton = true;
  }

  loadTawkToScript() {
    const s1 = document.createElement('script');
    const s0 = document.getElementsByTagName('script')[0];
    s1.async = true;
    s1.src = 'https://embed.tawk.to/650093ebb1aaa13b7a767595/1ha543l1o';
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');
    s1.onload = () => this.pollForTawkAPI();
    s1.onerror = () => console.error('Failed to load Tawk.to script.');
    s0.parentNode.insertBefore(s1, s0);
  }

  pollForTawkAPI(attempts = 10) {
    if (typeof Tawk_API !== 'undefined' && typeof Tawk_API.maximize === 'function') {
      this.isChatScriptLoaded = true;
      setTimeout(() => Tawk_API.maximize(), 100);
    } else if (attempts > 0) {
      setTimeout(() => this.pollForTawkAPI(attempts - 1), 300);
    } else {
      console.error('Tawk_API is not defined after several attempts.');
    }
  }

  checkButtonState() {
    if (!isPlatformBrowser(this.platformId)) return;
    const chatButtonClicked = localStorage.getItem('chatButtonClicked') === 'true';
    this.hideChatButton = chatButtonClicked;
    if (chatButtonClicked && typeof Tawk_API !== 'undefined' && typeof Tawk_API.maximize === 'function') {
      Tawk_API.maximize();
    } else {
      localStorage.setItem('chatButtonClicked', 'false');
    }
  }

  handleNavigationEnd(url: string) {
    this.applyChatWidgetVisibility(url);
    if (isPlatformBrowser(this.platformId)) {
      this.hideChatButton = url.includes('dashboard') || url.includes('auction-join') || url.includes('banner')
        ? true
        : localStorage.getItem('chatButtonClicked') === 'true';
    }
  }

  private applyChatWidgetVisibility(url: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const cleanUrl = (url || '').split('?')[0].split('#')[0];
    const hideWidget = cleanUrl === '/auction-join' || cleanUrl.startsWith('/auction-join/')
      || cleanUrl.startsWith('/dashboard/') || cleanUrl.startsWith('/banner');
    document.body.classList.toggle('hide-chat-widget', hideWidget);
  }
}
